import React, { useState, useRef } from "react";
import { Modal, Icon, ModalProps, Button, Select, StrictDropdownProps, DropdownItemProps, Message } from "semantic-ui-react";

import { useTranslation } from "react-i18next";
import { getPersonService } from "../services/personService";
import { apiService } from "../../store/index";
import _debounce from 'lodash/debounce';
import { useDelegationStatus } from "../store/selectors/observationSelectors";
import {
    isSubmitting, isSubmitted, isSubmitErrored,
    isLoaded, isLoading, TLoadable, createUnloaded, createLoading, createLoaded, createLoadErrored, createReloading, isReloading
} from "../../../sg-core";
import { useDispatch } from "react-redux";
import { createRequestDelegateObservationAction } from "../store/actions/observationActions";
import { Redirect } from "react-router-dom";
import { IUser } from "../../../sg-auth/models/User";
import { createShowNewMessageAction } from "../store/actions/messageActions";
import { makeAppMessage } from "../store/reducers/messageReducer";
import * as  Endpoint from "../../endpoints";
import { useAuthentication } from "../auth/store/authSelectors";
import { getRouteHrefById } from "../../../routing";
import { HavainnotRouteId } from "../../routes";

interface IDelegateProcessingModal {
    observationGuid: string;
    modalProps?: ModalProps
}

const DelegateProcessingModal: React.FC<IDelegateProcessingModal> = ({ observationGuid, modalProps = {} }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const { anonymousSession } = useAuthentication();
    const personService = getPersonService(apiService, Endpoint.havainnot, anonymousSession);
    const [personOptions, setPersonOptions] = useState<TLoadable<DropdownItemProps[]>>(createUnloaded());

    const handleSearch = async (searchQuery: string) => {
        try {
            const result = await personService.getPersonsToDelegate(searchQuery ? searchQuery + "" : "");
            if (result.isSuccess) {
                setPersonOptions(createLoaded(result.value))
            } else if (result.isFailure) {
                setPersonOptions(createLoadErrored(result.error));
            }
        } catch (err) {
            setPersonOptions(createLoadErrored(err as Error));
        }
    }

    const debouncedSeach = useRef(_debounce(handleSearch, 400)).current;

    const handleInputChange: StrictDropdownProps["onSearchChange"] = async (_1, { searchQuery }) => {
        const loadingState = isLoaded(personOptions) ? createReloading(personOptions.data) : createLoading();
        setPersonOptions(loadingState);
        debouncedSeach(searchQuery);
    }

    const [selectedPerson, setSelected] = useState<Pick<IUser, "fullname" | "email" | "id"> | undefined>();

    const handleDropdownClick = (_1: any, { value }: any) => {
        if (isLoaded(personOptions)) {
            const opt = personOptions.data.find((opt) => opt.value === value);
            opt && setSelected(opt.data);
        }
    }

    const handleDelegate = () => {
        if (selectedPerson) {
            dispatch(createRequestDelegateObservationAction(selectedPerson, observationGuid));
        }
    }

    const [isOpen, setOpen] = useState(false);
    const toggleModal = () => setOpen(!isOpen);

    const delegationStatus = useDelegationStatus();

    if (delegationStatus && isSubmitted(delegationStatus)) {

        const appMessage = makeAppMessage({
            header: t("Välittäminen onnistui"),
            content: `${t("Havainto välitettiin käsiteltäväksi käyttäjälle")} ${selectedPerson?.fullname}`,
            success: true
        });
        dispatch(createShowNewMessageAction(appMessage));
        return (
            <Redirect to={{
                pathname: getRouteHrefById(HavainnotRouteId.WorklistPage),
                state: { messageId: appMessage.id }
            }} />
        )
    }

    return (
        <Modal
            open={isOpen}
            onClose={() => setOpen(false)}
            trigger={<Button onClick={toggleModal}> {t("Välitä toiselle käsittelijälle")} </Button>}
            {...modalProps}
        >
            <Modal.Header> {t("Välitä käsittelijälle")} </Modal.Header>
            <Modal.Content>
                <Select
                    placeholder={t("Valitse käsittelijä")}
                    style={{ position: "relative", zIndex: 999 }}
                    onSearchChange={handleInputChange}
                    options={(isLoaded(personOptions) || isReloading(personOptions)) ? personOptions.data : []}
                    onChange={handleDropdownClick}
                    loading={isLoading(personOptions) || isReloading(personOptions)}
                    value={selectedPerson?.id}
                    fluid
                    search
                />

                <p> {t("Käsittelijä saa sähköpostin, jossa on linkki havaintoon")} </p>
                {selectedPerson && !selectedPerson.email && <Message warning> {t("Käyttäjällä ei ole sähköpostia johon lähettää ilmotusta")} </Message>}
                {delegationStatus && isSubmitErrored(delegationStatus) ? <Message negative> {t("Välittäminen ei onnistunut")} </Message> : null}
            </Modal.Content>
            <Modal.Actions>
                <Button
                    color="green"
                    icon
                    onClick={handleDelegate}
                    loading={delegationStatus && isSubmitting(delegationStatus)}
                    disabled={(delegationStatus && isSubmitting(delegationStatus)) || !selectedPerson}
                >
                    <Icon name="arrow right" /> {t("Välitä")}
                </Button>
                <Button type="button" onClick={toggleModal}>{t("Peruuta")}</Button>
            </Modal.Actions>
        </Modal>
    );
};


export default DelegateProcessingModal;
